import { useEffect, useState } from 'react';
import { create } from 'zustand';

import { pointer } from '../lib/pies.js';
import {
  activeContexts,
  formatChord,
  runCommand,
  setConflictHandler,
  shortcuts,
  useShortcutVersion,
  type Command,
} from '../lib/shortcuts/index.js';
import { useWorkspace } from '../state/workspace.js';
import { openShortcutsFor } from './SettingsView.js';
import { ContextMenu, type MenuItem } from './ui/controls.js';
import type { IconName } from './ui/icons.js';

/**
 * Unity's "Shortcut Conflict" prompt: a chord bound to several commands in the
 * same context asks which one to run, can make that choice permanent, or
 * opens the Shortcut Manager on the conflict.
 */
export function ShortcutConflictPrompt() {
  const [conflict, setConflict] = useState<{ chord: string; commands: Command[]; x: number; y: number } | null>(null);

  useEffect(() => {
    setConflictHandler((chord, commands) => setConflict({ chord, commands, x: pointer.x || 200, y: pointer.y || 120 }));
    return () => setConflictHandler(null);
  }, []);

  if (!conflict) return null;
  const { chord, commands } = conflict;
  const items: MenuItem[] = [
    { id: 'h', label: `${formatChord(chord)} runs ${commands.length} commands`, heading: true },
    ...commands.map((c) => ({ id: `run:${c.id}`, label: c.label, icon: c.icon as IconName | undefined, run: () => void runCommand(c.id) })),
    { id: 'sep', label: '', separator: true },
    ...commands.map((c) => ({
      id: `keep:${c.id}`,
      label: `Always use “${c.label}”`,
      icon: 'check' as IconName,
      run: () => {
        // Rebind: the chord stays only on the chosen command.
        for (const other of commands)
          if (other.id !== c.id) shortcuts.setKeys(other.id, shortcuts.keysFor(other.id).filter((k) => k !== chord));
        runCommand(c.id);
      },
    })),
    { id: 'sep2', label: '', separator: true },
    { id: 'manager', label: 'Resolve in Shortcut Manager…', icon: 'command', run: () => openShortcutsFor(chord) },
  ];
  return <ContextMenu x={conflict.x} y={conflict.y} items={items} onClose={() => setConflict(null)} />;
}

const HELPER_KEY = 'ai-studio:helper-bar';

export const useHelperBar = create<{ visible: boolean; toggle: () => void }>((set, get) => ({
  visible: (() => {
    try {
      return localStorage.getItem(HELPER_KEY) !== 'off';
    } catch {
      return true;
    }
  })(),
  toggle: () => {
    const visible = !get().visible;
    set({ visible });
    try {
      localStorage.setItem(HELPER_KEY, visible ? 'on' : 'off');
    } catch {
      // not remembered
    }
  },
}));

/** Global hints always worth showing, after the focused view's own keys. */
const GLOBAL_HINTS = ['search.open', 'favorites.open', 'pie.tabs', 'layout.maximize', 'view.history'];

/**
 * Shortcut Helper Bar (Unity 6's status-bar helper, Blender's status-bar key
 * hints): the keys that work right now in the focused view, deepest context
 * first, then a few global ones.
 */
export function ShortcutHelperBar() {
  useShortcutVersion();
  const visible = useHelperBar((s) => s.visible);
  const view = useWorkspace((s) => s.view);
  const [, tick] = useState(0);
  // Sub-contexts (e.g. Rig & Animate) are not React state: refresh when focus moves.
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!visible) return null;

  const contexts = activeContexts().filter((c) => c !== 'global');
  const rows: Array<{ id: string; key: string; label: string; external: boolean }> = [];
  const seen = new Set<string>();
  const add = (c: Command | undefined) => {
    if (!c || c.hidden) return;
    const key = shortcuts.keysFor(c.id)[0];
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push({ id: c.id, key, label: c.label.replace(/ tool$/i, ''), external: Boolean(c.external) });
  };
  for (const context of contexts) for (const c of shortcuts.list().filter((x) => x.context === context)) if (rows.length < 9) add(c);
  for (const id of GLOBAL_HINTS) add(shortcuts.get(id));

  return (
    <footer className="helper-bar" aria-label={`Shortcuts for ${view}`}>
      {rows.map((r) => (
        <button
          key={r.id}
          type="button"
          className="helper-item"
          title={`${r.label} (${formatChord(r.key)})`}
          disabled={r.external}
          onClick={() => runCommand(r.id)}
        >
          <kbd className="kbd">{formatChord(r.key)}</kbd>
          <span>{r.label}</span>
        </button>
      ))}
      <span className="helper-fill" />
      <button type="button" className="helper-item helper-close" title="Hide the Shortcut Helper Bar" onClick={() => useHelperBar.getState().toggle()}>
        ×
      </button>
    </footer>
  );
}
